
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Edit, Star, Trash2 } from "lucide-react";
import { EpisodeWithCategory } from "./types";
import { VideoPreview } from "./components/VideoPreview";

interface EpisodeCardProps {
  episode: EpisodeWithCategory;
  onEdit: (episode: EpisodeWithCategory) => void;
  onDelete: (episodeId: string) => void;
}

export const EpisodeCard = ({ episode, onEdit, onDelete }: EpisodeCardProps) => {
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete the episode "${episode.title}"?`)) {
      onDelete(episode.id);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Episode Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold">{episode.title}</h3>
              {episode.featured && (
                <Star className="h-4 w-4 text-yellow-500 fill-current" />
              )}
            </div>
            <p className="text-gray-600 mt-1 mb-3">{episode.description}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              {episode.episodes_categories && (
                <span 
                  className="px-2 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: episode.episodes_categories.color || '#3B82F6' }}
                >
                  {episode.episodes_categories.name}
                </span>
              )}
              <span>Duration: {episode.duration}</span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                episode.status === 'published' ? 'bg-green-100 text-green-800' :
                episode.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {episode.status}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(episode)}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>

          {/* Video Preview */}
          <div className="lg:col-span-1">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Video Preview</h4>
              <VideoPreview
                youtubeId={episode.youtube_id || undefined}
                vimeoId={episode.vimeo_id || undefined}
                videoPlatform={episode.video_platform as "youtube" | "vimeo" | undefined}
                title={episode.title}
                thumbnailPath={episode.thumbnail_url || undefined}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
